<template>
  <div @click="selectConversation" :class="[
    'group relative flex gap-3 cursor-pointer transition-all duration-200 ease-out',
    'border-b dark:border-gray-700/50 px-4 py-3',
    'hover:shadow-sm',
    conversation?.id == currentInbox?.id
      ? 'bg-blue-50 dark:bg-blue-900/10 border-l-4'
      : 'bg-white dark:bg-slate-800 border-l-4 border-l-transparent hover:border-l-gray-300 dark:hover:border-l-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700/50'
  ]" :style="conversation?.id == currentInbox?.id ? { borderLeftColor: conversation.agent?.color ?? '#3b82f6' } : {}">

    <!-- Avatar avec statut -->
    <div class="relative flex-shrink-0">
      <img v-if="conversation?.profile" :src="conversation.profile" :alt="conversation.name || 'Contact'"
        @error="handleImageError" class="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700" />
      <div v-else
        class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700 flex items-center justify-center ring-2 ring-gray-100 dark:ring-gray-700">
        <i class="las la-user text-white text-2xl"></i>
      </div>

      <!-- Badge non lu (sur l'avatar) -->
      <span v-if="conversation.is_unread"
        class="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 border-2 border-white dark:border-slate-800 rounded-full flex items-center justify-center">
        <span class="w-2 h-2 bg-white rounded-full animate-pulse"></span>
      </span>
    </div>

    <!-- Contenu principal -->
    <div class="flex-1 min-w-0">
      <!-- En-tête -->
      <div class="flex items-baseline justify-between mb-1">
        <h3 class="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm">
          {{ inbox?.phone?.split("@")[0] ?? "Inconnu" }}
          <span v-if="conversation?.name" class="font-normal text-xs text-gray-500 dark:text-gray-400 ml-1">
            • {{ conversation.name }}
          </span>
        </h3>

        <!-- Heure -->
        <time class="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
          {{ setDateFormat(lastmessage?.date) ?? "—" }}
        </time>
      </div>

      <!-- Dernier message -->
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-1.5 min-w-0 flex-1">
          <!-- Indicateurs de statut -->
          <i v-if="lastmessage?.message_id?.startsWith('true')"
            class="las la-check-double text-teal-600 text-sm flex-shrink-0" title="Message lu"></i>
          <i v-if="lastmessage?.isForwarded" class="fas fa-share text-gray-400 text-xs flex-shrink-0"
            title="Transféré"></i>

          <!-- Icône du type de message -->
          <i v-if="lastmessage?.type !== 'chat'" :class="getMediaIcon(lastmessage?.type)"
            class="text-gray-500 dark:text-gray-400 text-sm flex-shrink-0"></i>

          <!-- Texte du message -->
          <p :class="[
            'text-sm truncate',
            !lastmessage?.selfMessage && !lastmessage?.isRead
              ? 'text-gray-900 dark:text-gray-100 font-medium'
              : 'text-gray-600 dark:text-gray-400'
          ]">
            <span v-if="lastmessage?.type !== 'chat'">
              {{ getMediaLabel(lastmessage?.type) }}
            </span>
            <span v-else>
              {{ JSON.parse(lastmessage?.content) ?? "" }}
            </span>
          </p>
        </div>

        <!-- Badges à droite -->
        <div class="flex items-center gap-1.5 flex-shrink-0">
          <!-- Badge clôturé -->
          <span v-if="conversation.status === 'close'"
            class="px-2 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
            Clôturé
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toPascalCase } from "@/utils";
import moment from "moment";
import { mapActions } from "vuex";
import { mapFields } from "vuex-map-fields";

export default {
  name: "InboxConversation",
  props: {
    conversation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      imageError: false
    }
  },
  computed: {
    inbox() {
      return this.conversation?.inbox ?? null;
    },
    lastmessage() {
      return this.conversation?.latest_message ?? null;
    },
    ...mapFields("conversations/show", {
      currentInbox: "item",
      currentItem: "currentItem",
      filter: "filter"
    }),
    ...mapFields("messages/list", {
      messageFilter: "filter"
    }),
  },
  methods: {
    toPascalCase,
    ...mapActions({
      getConversation: "conversations/show/getOne",
      resetConversation: "conversations/show/resetData",
      getMessages: "messages/list/getAll",
      resetMessages: "messages/list/resetData",
      resetReply: "messages/reply/resetData",
      resetTicket: "tickets/create/resetData",
    }),

    handleImageError() {
      this.imageError = true;
    },

    selectConversation() {
      if (this.conversation?.id != this.filter.conversation_id) {
        this.filter.conversation_id = this.conversation?.id;
        this.currentItem = this.conversation;
        this.messageFilter.conversation_id = this.conversation?.id;
        this.resetReply();
        this.resetTicket();
        this.getConversation();
      }
    },

    getMediaIcon(type) {
      const icons = {
        audio: 'fas fa-music',
        image: 'fas fa-image',
        video: 'fas fa-video',
        ptt: 'fas fa-microphone',
        location: 'fas fa-map-marker-alt',
        vcard: 'fas fa-address-card',
        document: 'fas fa-file-alt'
      };
      return icons[type] || 'fas fa-file';
    },

    getMediaLabel(type) {
      const labels = {
        ptt: 'Message vocal',
        audio: 'Audio',
        image: 'Photo',
        video: 'Vidéo',
        location: 'Position',
        vcard: 'Contact',
        document: 'Document'
      };
      return labels[type] || toPascalCase(type ?? "");
    },

    setDateFormat(date) {
      if (!date) return "—";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
  },
  unmounted() {
    this.resetConversation();
    this.resetMessages();
  },
};
</script>